import React, { useEffect, useState } from "react";
import { Link } from "../../router";
import { api } from "../../api";

function CourseSettingsTab({ course, onSaved }) {
  const [form, setForm] = useState({
    title: course.title,
    subtitle: course.subtitle || "",
    description: course.description || "",
    coverImageUrl: course.cover_image_url || "",
    // Stored in the database (and sent to Stripe) as integer cents, but
    // admins think and type in dollars - so the form works in dollars and
    // converts to cents only at the save boundary (see `save` below).
    priceDollars: course.price_cents ? course.price_cents / 100 : 0,
    billingType: course.billing_type,
    billingInterval: course.billing_interval || "month",
    accessDurationDays: course.access_duration_days || "",
    published: !!course.published,
    voucherCode: course.voucher_code || "",
    voucherMaxUses: course.voucher_max_uses || "",
  });
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      let coverImageUrl = form.coverImageUrl;
      if (coverImageFile) {
        const uploadForm = new FormData();
        uploadForm.append("file", coverImageFile);
        const uploaded = await api.post("/api/admin/upload", uploadForm);
        coverImageUrl = uploaded.url;
      }
      const priceCents = Math.round(Number(form.priceDollars || 0) * 100);
      const updated = await api.put(`/api/admin/courses/${course.id}`, { ...form, coverImageUrl, priceCents });
      setCoverImageFile(null);
      onSaved(updated.course);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = coverImageFile ? URL.createObjectURL(coverImageFile) : form.coverImageUrl;

  return (
    <form onSubmit={save} className="admin-form">
      {error && <div className="alert alert-error">{error}</div>}
      <label>
        Title
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </label>
      <label>
        Subtitle
        <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
      </label>
      <label>
        Description
        <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </label>
      <label>
        Course thumbnail
        {previewUrl && (
          <div className="course-thumbnail-preview" style={{ backgroundImage: `url(${previewUrl})` }} />
        )}
        <input type="file" accept="image/*" onChange={(e) => setCoverImageFile(e.target.files[0])} />
        <span className="muted" style={{ fontWeight: 400 }}>
          Shown on the course tile on the homepage. Leave unset for a plain placeholder tile.
        </span>
      </label>
      <div style={{ display: "flex", gap: 16 }}>
        <label style={{ flex: 1 }}>
          Price (in dollars)
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.priceDollars}
            onChange={(e) => setForm({ ...form, priceDollars: e.target.value === "" ? "" : Number(e.target.value) })}
          />
        </label>
        <label style={{ flex: 1 }}>
          Billing type
          <select value={form.billingType} onChange={(e) => setForm({ ...form, billingType: e.target.value })}>
            <option value="one_time">One-time purchase</option>
            <option value="subscription">Subscription</option>
          </select>
        </label>
        {form.billingType === "subscription" && (
          <label style={{ flex: 1 }}>
            Billing interval
            <select value={form.billingInterval} onChange={(e) => setForm({ ...form, billingInterval: e.target.value })}>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </label>
        )}
      </div>
      <label>
        Access duration (days)
        <input
          type="number"
          min={0}
          placeholder="Leave blank for lifetime access"
          value={form.accessDurationDays}
          onChange={(e) => setForm({ ...form, accessDurationDays: e.target.value === "" ? "" : Number(e.target.value) })}
        />
        <span className="muted" style={{ fontWeight: 400 }}>
          If set, a student's access ends this many days after they enroll - regardless of how much of the
          course they've completed. Leave blank for lifetime access.
        </span>
      </label>
      <div style={{ display: "flex", gap: 16 }}>
        <label style={{ flex: 1 }}>
          Voucher code
          <input
            placeholder="e.g. LAUNCH2026"
            value={form.voucherCode}
            onChange={(e) => setForm({ ...form, voucherCode: e.target.value })}
          />
        </label>
        <label style={{ flex: 1 }}>
          Max number of enrollments
          <input
            type="number"
            min={1}
            placeholder="Blank = unlimited"
            value={form.voucherMaxUses}
            onChange={(e) => setForm({ ...form, voucherMaxUses: e.target.value === "" ? "" : Number(e.target.value) })}
          />
        </label>
      </div>
      <span className="muted" style={{ fontWeight: 400, marginTop: -12, display: "block" }}>
        A student who enters this code on the course page enrolls immediately, completely bypassing payment.
        {course.voucher_code ? ` ${course.voucher_uses_count || 0} used so far` : ""}
        {course.voucher_code && course.voucher_max_uses ? ` of ${course.voucher_max_uses}.` : course.voucher_code ? "." : ""}
      </span>
      <label className="checkbox-row">
        <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
        Published (visible to students)
      </label>
      <button className="btn btn-primary" disabled={busy}>
        {busy ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}

// Edits an existing content block in place (the add form above only creates
// new ones - there was previously no way to fix a typo or swap a file
// without deleting and re-adding the whole block).
function EditContentBlockForm({ block, onSaved, onCancel }) {
  const [html, setHtml] = useState(block.type === "text" ? block.data.html || "" : "");
  const [videoUrl, setVideoUrl] = useState(block.type === "video" ? block.data.url || "" : "");
  const [taskInstructions, setTaskInstructions] = useState(block.type === "task" ? block.data.instructions || "" : "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      let data = block.data;
      if (block.type === "text") data = { html };
      if (block.type === "video") data = { url: videoUrl, provider: /youtube|vimeo/.test(videoUrl) ? "embed" : "file" };
      if (block.type === "task") data = { instructions: taskInstructions };
      if ((block.type === "pdf" || block.type === "download") && file) {
        const form = new FormData();
        form.append("file", file);
        const uploaded = await api.post("/api/admin/upload", form);
        data = { url: uploaded.url, fileName: uploaded.fileName };
      }
      await api.put(`/api/admin/content-blocks/${block.id}`, { data });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="content-block-form content-block-edit-form">
      {error && <div className="alert alert-error">{error}</div>}
      {block.type === "text" && (
        <textarea placeholder="Lesson text (basic HTML allowed)" rows={3} value={html} onChange={(e) => setHtml(e.target.value)} />
      )}
      {block.type === "video" && (
        <input
          placeholder="Video URL (YouTube/Vimeo link, or direct .mp4 URL)"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
        />
      )}
      {block.type === "task" && (
        <textarea placeholder="Task instructions" rows={2} value={taskInstructions} onChange={(e) => setTaskInstructions(e.target.value)} />
      )}
      {(block.type === "pdf" || block.type === "download") && (
        <>
          <span className="muted">Current file: {block.data.fileName || "(none)"} - choose a new one to replace it, or leave blank to keep it.</span>
          <input type="file" onChange={(e) => setFile(e.target.files[0])} />
        </>
      )}
      <button className="btn btn-primary btn-sm" disabled={busy}>
        {busy ? "Saving…" : "Save"}
      </button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </form>
  );
}

function AddContentBlockForm({ lessonId, onAdded }) {
  const [type, setType] = useState("text");
  const [text, setText] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [taskInstructions, setTaskInstructions] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      let data = {};
      if (type === "text") data = { html: `<p>${text}</p>` };
      if (type === "video") data = { url: videoUrl, provider: /youtube|vimeo/.test(videoUrl) ? "embed" : "file" };
      if (type === "task") data = { instructions: taskInstructions };
      if (type === "pdf" || type === "download") {
        if (!file) throw new Error("Choose a file to upload");
        const form = new FormData();
        form.append("file", file);
        const uploaded = await api.post("/api/admin/upload", form);
        data = { url: uploaded.url, fileName: uploaded.fileName };
      }
      await api.post(`/api/admin/lessons/${lessonId}/content-blocks`, { type, data });
      setText("");
      setVideoUrl("");
      setTaskInstructions("");
      setFile(null);
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="content-block-form">
      {error && <div className="alert alert-error">{error}</div>}
      <select value={type} onChange={(e) => setType(e.target.value)}>
        <option value="text">Text</option>
        <option value="video">Video</option>
        <option value="pdf">PDF</option>
        <option value="download">Downloadable file</option>
        <option value="task">Task</option>
      </select>
      {type === "text" && (
        <textarea placeholder="Lesson text (basic HTML allowed)" rows={2} value={text} onChange={(e) => setText(e.target.value)} />
      )}
      {type === "video" && (
        <input
          placeholder="Video URL (YouTube/Vimeo link, or direct .mp4 URL)"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
        />
      )}
      {type === "task" && (
        <textarea placeholder="Task instructions" rows={2} value={taskInstructions} onChange={(e) => setTaskInstructions(e.target.value)} />
      )}
      {(type === "pdf" || type === "download") && (
        <input type="file" onChange={(e) => setFile(e.target.files[0])} />
      )}
      <button className="btn btn-secondary btn-sm" disabled={busy}>
        {busy ? "Adding…" : "Add content block"}
      </button>
    </form>
  );
}

// Small inline-rename control used for both chapter and lesson titles.
function EditableTitle({ title, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(title);
  }, [title]);

  if (!editing) {
    return (
      <span className="editable-title">
        {title}
        <button type="button" className="link-btn edit-title-btn" onClick={() => setEditing(true)} title="Rename">
          ✎
        </button>
      </span>
    );
  }

  const cancel = () => {
    setValue(title);
    setEditing(false);
  };

  const save = async () => {
    if (!value.trim() || value.trim() === title) {
      cancel();
      return;
    }
    setBusy(true);
    try {
      await onSave(value.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="editable-title-form">
      <input
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
      />
      <button type="button" className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
        {busy ? "…" : "Save"}
      </button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={cancel} disabled={busy}>
        Cancel
      </button>
    </span>
  );
}

// Shared unlock-rule form body (dependency picker + delay + match mode).
// `dependencyOptions` is the earlier-in-course list to pick from;
// `dependencyLabel` distinguishes "section"/"chapter" wording.
function UnlockRuleFields({ dependsOn, setDependsOn, delayType, setDelayType, delayValue, setDelayValue, matchMode, setMatchMode, dependencyOptions, dependencyLabel }) {
  const bothConfigured = !!dependsOn && delayType !== "none";
  return (
    <>
      <label>
        Depends on
        <select value={dependsOn} onChange={(e) => setDependsOn(e.target.value)}>
          <option value="">No {dependencyLabel} dependency</option>
          {dependencyOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <div className="unlock-rule-delay">
        <span className="muted">and wait</span>
        <select value={delayType} onChange={(e) => setDelayType(e.target.value)}>
          <option value="none">no additional time</option>
          <option value="hours">a number of hours</option>
          <option value="next_day">until the next day (student's local midnight)</option>
          <option value="days">a number of days (counted from the dependency above being completed)</option>
          <option value="days_from_enrollment">a number of days from the start of the course (enrollment date)</option>
        </select>
        {(delayType === "days" || delayType === "hours" || delayType === "days_from_enrollment") && (
          <input
            type="number"
            min={1}
            style={{ width: 70 }}
            value={delayValue}
            onChange={(e) => setDelayValue(Number(e.target.value))}
          />
        )}
      </div>
      {delayType === "days_from_enrollment" && (
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          This counts from when the student enrolled (the course start date), not from any dependency above being
          completed - use this for a rule like "this chapter opens 14 days after the course starts" regardless of
          progress.
        </p>
      )}
      {bothConfigured && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400 }}>
          <span className="muted">Unlock when:</span>
          <select value={matchMode} onChange={(e) => setMatchMode(e.target.value)}>
            <option value="all">both conditions are met</option>
            <option value="any">either condition is met</option>
          </select>
        </label>
      )}
    </>
  );
}

// Compares the editor's live form state against what's actually persisted on
// the lesson/chapter row, so the admin gets a visible warning whenever
// there's an unsaved change - this is the fix for drip rules that LOOKED
// configured in the dropdown but were never actually saved to the database.
function isUnlockRuleDirty(item, dependsOnKey, { dependsOn, delayType, delayValue, matchMode }) {
  const persistedDependsOn = item[dependsOnKey] ?? null;
  const formDependsOn = dependsOn ? Number(dependsOn) : null;
  const persistedDelayType = item.unlock_delay_type || "none";
  const persistedDelayValue = item.unlock_delay_value ?? null;
  const persistedMatchMode = item.unlock_match_mode || "all";
  if (persistedDependsOn !== formDependsOn) return true;
  if (persistedDelayType !== delayType) return true;
  if (delayType !== "none" && Number(persistedDelayValue) !== Number(delayValue)) return true;
  if (persistedMatchMode !== matchMode) return true;
  return false;
}

function UnlockRuleEditor({ lesson, isFirstLesson, dependencyOptions, onChanged }) {
  // Reflect exactly what's persisted - new lessons already get a real
  // dependency saved at creation time (see the create-lesson endpoint), so
  // by the time we get here a null value means an admin genuinely chose "no
  // dependency" and saved it, not "never configured." Falling back to a
  // suggested default here (as this used to do) would make that legitimate
  // choice look unsaved.
  const initial = lesson.unlock_depends_on_lesson_id;
  const [dependsOn, setDependsOn] = useState(initial === null || initial === undefined ? "" : String(initial));
  const [delayType, setDelayType] = useState(lesson.unlock_delay_type || "none");
  const [delayValue, setDelayValue] = useState(lesson.unlock_delay_value || 1);
  const [matchMode, setMatchMode] = useState(lesson.unlock_match_mode || "all");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  if (isFirstLesson) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        This is the course's first lesson, so it's always available immediately - there's nothing before it to gate on.
      </p>
    );
  }

  const dirty = isUnlockRuleDirty(lesson, "unlock_depends_on_lesson_id", { dependsOn, delayType, delayValue, matchMode });

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.put(`/api/admin/lessons/${lesson.id}`, {
        unlockDependsOnLessonId: dependsOn || null,
        unlockDelayType: delayType,
        unlockDelayValue: delayType === "none" ? null : delayValue,
        unlockMatchMode: matchMode,
      });
      setSaved(true);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-rule-editor">
      <UnlockRuleFields
        dependsOn={dependsOn}
        setDependsOn={setDependsOn}
        delayType={delayType}
        setDelayType={setDelayType}
        delayValue={delayValue}
        setDelayValue={setDelayValue}
        matchMode={matchMode}
        setMatchMode={setMatchMode}
        dependencyOptions={dependencyOptions}
        dependencyLabel="section"
      />
      {dirty && !saved && (
        <div className="alert alert-warning unlock-unsaved-warning">
          ⚠ Not saved yet - this rule won't take effect for students until you click "Save unlock rule" below.
        </div>
      )}
      <button className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
        {busy ? "Saving…" : dirty ? "Save unlock rule" : "Saved ✓"}
      </button>
    </div>
  );
}

function ChapterUnlockRuleEditor({ chapter, isFirstChapter, dependencyOptions, onChanged }) {
  // See the matching comment in UnlockRuleEditor above - this reflects the
  // real persisted value rather than falling back to a suggested default,
  // since new chapters already get a real dependency saved at creation
  // time now.
  const initial = chapter.unlock_depends_on_chapter_id;
  const [dependsOn, setDependsOn] = useState(initial === null || initial === undefined ? "" : String(initial));
  const [delayType, setDelayType] = useState(chapter.unlock_delay_type || "none");
  const [delayValue, setDelayValue] = useState(chapter.unlock_delay_value || 1);
  const [matchMode, setMatchMode] = useState(chapter.unlock_match_mode || "all");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  if (isFirstChapter) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        This is the course's first chapter, so it's always available immediately.
      </p>
    );
  }

  const dirty = isUnlockRuleDirty(chapter, "unlock_depends_on_chapter_id", { dependsOn, delayType, delayValue, matchMode });

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.put(`/api/admin/chapters/${chapter.id}`, {
        unlockDependsOnChapterId: dependsOn || null,
        unlockDelayType: delayType,
        unlockDelayValue: delayType === "none" ? null : delayValue,
        unlockMatchMode: matchMode,
      });
      setSaved(true);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-rule-editor">
      <UnlockRuleFields
        dependsOn={dependsOn}
        setDependsOn={setDependsOn}
        delayType={delayType}
        setDelayType={setDelayType}
        delayValue={delayValue}
        setDelayValue={setDelayValue}
        matchMode={matchMode}
        setMatchMode={setMatchMode}
        dependencyOptions={dependencyOptions}
        dependencyLabel="chapter"
      />
      {dirty && !saved && (
        <div className="alert alert-warning unlock-unsaved-warning">
          ⚠ Not saved yet - this rule won't take effect for students until you click "Save unlock rule" below.
        </div>
      )}
      <button className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
        {busy ? "Saving…" : dirty ? "Save unlock rule" : "Saved ✓"}
      </button>
    </div>
  );
}

// Course-wide bulk apply: configure one rule and push it onto every chapter
// and/or every lesson at once, instead of visiting each one individually.
function CourseDripDefaults({ courseId, onApplied }) {
  const [scope, setScope] = useState("both");
  const [dependsOnPrevious, setDependsOnPrevious] = useState(true);
  const [delayType, setDelayType] = useState("none");
  const [delayValue, setDelayValue] = useState(1);
  const [matchMode, setMatchMode] = useState("all");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const bothConfigured = dependsOnPrevious && delayType !== "none";

  const apply = async () => {
    if (
      !window.confirm(
        "This overwrites the unlock rule on every chapter/section in the scope you picked below. Continue?"
      )
    )
      return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api.put(`/api/admin/courses/${courseId}/drip-defaults`, {
        scope,
        dependsOnPrevious,
        delayType,
        delayValue: delayType === "none" ? null : delayValue,
        matchMode,
      });
      setResult(res);
      onApplied();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="drip-defaults-panel">
      <div className="muted" style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, marginBottom: 6 }}>
        Bulk drip rule
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Apply one unlock rule to every chapter and/or section in this course at once, instead of configuring each
        one individually. The very first chapter/lesson always stays open, since nothing precedes it.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {result && (
        <div className="alert alert-success">
          Applied to {result.chaptersUpdated} chapter(s) and {result.lessonsUpdated} section(s).
        </div>
      )}
      <label>
        Apply to
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="both">All chapters and sections (whole course)</option>
          <option value="chapters">All chapters only</option>
          <option value="lessons">All sections only</option>
        </select>
      </label>
      <label className="checkbox-row">
        <input type="checkbox" checked={dependsOnPrevious} onChange={(e) => setDependsOnPrevious(e.target.checked)} />
        Require the previous chapter/section to be marked complete
      </label>
      <div className="unlock-rule-delay">
        <span className="muted">and wait</span>
        <select value={delayType} onChange={(e) => setDelayType(e.target.value)}>
          <option value="none">no additional time</option>
          <option value="hours">a number of hours</option>
          <option value="next_day">until the next day (student's local midnight)</option>
          <option value="days">a number of days (counted from the dependency above being completed)</option>
          <option value="days_from_enrollment">a number of days from the start of the course (enrollment date)</option>
        </select>
        {(delayType === "days" || delayType === "hours" || delayType === "days_from_enrollment") && (
          <input
            type="number"
            min={1}
            style={{ width: 70 }}
            value={delayValue}
            onChange={(e) => setDelayValue(Number(e.target.value))}
          />
        )}
      </div>
      {bothConfigured && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400 }}>
          <span className="muted">Unlock when:</span>
          <select value={matchMode} onChange={(e) => setMatchMode(e.target.value)}>
            <option value="all">both conditions are met</option>
            <option value="any">either condition is met</option>
          </select>
        </label>
      )}
      <button className="btn btn-secondary btn-sm" onClick={apply} disabled={busy}>
        {busy ? "Applying…" : "Apply to course"}
      </button>
    </div>
  );
}

// Configures the minimum time (in minutes) a student must spend on this
// lesson - tab open and visible - before "Mark lesson complete" enables.
// Blank/0 means no minimum (the button is available right away, as before).
function WatchTimeRuleEditor({ lesson, onChanged }) {
  const [minutes, setMinutes] = useState(lesson.min_watch_minutes ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const persisted = lesson.min_watch_minutes ?? null;
  const current = minutes === "" ? null : Number(minutes);
  const dirty = persisted !== current;

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.put(`/api/admin/lessons/${lesson.id}`, { minWatchMinutes: current });
      setSaved(true);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-rule-editor">
      <label>
        Minimum minutes before "Mark complete" is enabled
        <input
          type="number"
          min={0}
          placeholder="Blank = no minimum"
          style={{ maxWidth: 160 }}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
        />
      </label>
      <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
        Counts actual time spent with this lesson open and the browser tab visible, so students can't skip
        straight to "complete." Leaving and coming back later resumes from where they left off rather than
        resetting or counting the time away.
      </p>
      <button className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
        {busy ? "Saving…" : dirty ? "Save" : "Saved ✓"}
      </button>
    </div>
  );
}

// Add/edit form for a single quiz question - shared by lesson quizzes and
// the course final exam, since both are just `quizzes` rows underneath.
// True/false questions get fixed True/False option text (not editable);
// multiple choice lets the admin add/remove options freely (min 2).
function QuizQuestionForm({ quizId, question, onSaved, onCancel }) {
  const isEdit = !!question;
  const [type, setType] = useState(question?.type || "multiple_choice");
  const [prompt, setPrompt] = useState(question?.prompt || "");
  const [options, setOptions] = useState(
    question
      ? question.options.map((o) => ({ text: o.text, isCorrect: o.isCorrect }))
      : [
          { text: "", isCorrect: true },
          { text: "", isCorrect: false },
        ]
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const changeType = (nextType) => {
    setType(nextType);
    if (nextType === "true_false") {
      setOptions([
        { text: "True", isCorrect: options[0]?.isCorrect ?? true },
        { text: "False", isCorrect: !(options[0]?.isCorrect ?? true) },
      ]);
    }
  };

  const updateOptionText = (idx, text) => setOptions(options.map((o, i) => (i === idx ? { ...o, text } : o)));
  const setCorrect = (idx) => setOptions(options.map((o, i) => ({ ...o, isCorrect: i === idx })));
  const addOption = () => setOptions([...options, { text: "", isCorrect: false }]);
  const removeOption = (idx) => setOptions(options.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!prompt.trim()) throw new Error("Question text is required");
      const payload = { type, prompt: prompt.trim(), options };
      if (isEdit) {
        await api.put(`/api/admin/quiz-questions/${question.id}`, payload);
      } else {
        await api.post(`/api/admin/quizzes/${quizId}/questions`, payload);
        setPrompt("");
        setOptions(
          type === "true_false"
            ? [
                { text: "True", isCorrect: true },
                { text: "False", isCorrect: false },
              ]
            : [
                { text: "", isCorrect: true },
                { text: "", isCorrect: false },
              ]
        );
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="content-block-form quiz-question-form">
      {error && <div className="alert alert-error">{error}</div>}
      <div style={{ display: "flex", gap: 8, width: "100%" }}>
        <select value={type} onChange={(e) => changeType(e.target.value)} style={{ maxWidth: 190, minWidth: 150 }}>
          <option value="multiple_choice">Multiple choice</option>
          <option value="true_false">True / False</option>
        </select>
        <textarea
          placeholder="Question text"
          rows={2}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={{ flex: 1 }}
        />
      </div>
      <div className="quiz-options-editor">
        {options.map((o, idx) => (
          <div key={idx} className="quiz-option-editor-row">
            <input
              type="radio"
              name={`correct-${question ? question.id : "new"}`}
              checked={o.isCorrect}
              onChange={() => setCorrect(idx)}
              title="Correct answer"
              style={{ width: "auto" }}
            />
            <input
              placeholder={`Option ${idx + 1}`}
              value={o.text}
              disabled={type === "true_false"}
              onChange={(e) => updateOptionText(idx, e.target.value)}
              style={{ flex: 1 }}
            />
            {type === "multiple_choice" && options.length > 2 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeOption(idx)}>
                ✕
              </button>
            )}
          </div>
        ))}
        {type === "multiple_choice" && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={addOption} style={{ alignSelf: "flex-start" }}>
            + Add option
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-secondary btn-sm" disabled={busy}>
          {busy ? "Saving…" : isEdit ? "Save question" : "Add question"}
        </button>
        {isEdit && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

// Enable/configure an optional quiz (pass percent + questions) attached
// either to one lesson or, as the course's final exam, to the whole course.
// Mirrors WatchTimeRuleEditor's dirty-tracked settings pattern for the
// enabled/pass-percent toggle, plus AddContentBlockForm's immediate-save
// list pattern for the questions themselves.
function QuizEditor({ quiz, settingsUrl, onChanged, title, enableLabel }) {
  const [enabled, setEnabled] = useState(!!quiz);
  const [passPercent, setPassPercent] = useState(quiz ? quiz.pass_percent : 70);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  useEffect(() => {
    setEnabled(!!quiz);
    setPassPercent(quiz ? quiz.pass_percent : 70);
  }, [quiz?.id, quiz?.pass_percent]);

  const dirty = enabled !== !!quiz || (enabled && Number(passPercent) !== Number(quiz ? quiz.pass_percent : 70));

  const saveSettings = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.put(settingsUrl, { enabled, passPercent: Number(passPercent) || 70 });
      setSaved(true);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    await api.del(`/api/admin/quiz-questions/${questionId}`);
    onChanged();
  };

  return (
    <div className="unlock-rule-section">
      <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
        {title}
      </div>
      <div className="unlock-rule-editor">
        <label className="checkbox-row">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {enableLabel}
        </label>
        {enabled && (
          <label style={{ maxWidth: 220 }}>
            Passing score (%)
            <input
              type="number"
              min={1}
              max={100}
              value={passPercent}
              onChange={(e) => setPassPercent(e.target.value)}
            />
          </label>
        )}
        <button className="btn btn-secondary btn-sm" onClick={saveSettings} disabled={busy}>
          {busy ? "Saving…" : dirty ? "Save" : "Saved ✓"}
        </button>
      </div>

      {quiz && (
        <div style={{ marginTop: 14 }}>
          {quiz.questions.length === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              No questions yet - students can't take this until you add at least one.
            </p>
          )}
          {quiz.questions.map((q) =>
            editingQuestionId === q.id ? (
              <QuizQuestionForm
                key={q.id}
                question={q}
                onSaved={() => {
                  setEditingQuestionId(null);
                  onChanged();
                }}
                onCancel={() => setEditingQuestionId(null)}
              />
            ) : (
              <div className="admin-block-row" key={q.id}>
                <span className="badge">{q.type === "true_false" ? "True/False" : "Multiple choice"}</span>
                <span className="muted admin-block-preview">{q.prompt}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingQuestionId(q.id)}>
                  Edit
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => deleteQuestion(q.id)}>
                  Remove
                </button>
              </div>
            )
          )}
          <QuizQuestionForm quizId={quiz.id} onSaved={onChanged} />
        </div>
      )}
    </div>
  );
}

function LessonRow({ lesson, meta, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);

  const deleteLesson = async () => {
    if (!window.confirm(`Delete lesson "${lesson.title}"?`)) return;
    await api.del(`/api/admin/lessons/${lesson.id}`);
    onChanged();
  };

  const deleteBlock = async (blockId) => {
    await api.del(`/api/admin/content-blocks/${blockId}`);
    onChanged();
  };

  const renameLesson = (title) => api.put(`/api/admin/lessons/${lesson.id}`, { title }).then(onChanged);

  return (
    <div className="admin-lesson">
      <div className="admin-lesson-row">
        <button className="link-btn expand-toggle" onClick={() => setExpanded(!expanded)}>
          {expanded ? "▾" : "▸"}
        </button>
        <span className="admin-lesson-title">
          <EditableTitle title={lesson.title} onSave={renameLesson} />
        </span>
        <span className="muted">{lesson.contentBlocks.length} block(s)</span>
        <button className="btn btn-ghost btn-sm" onClick={deleteLesson}>
          Delete
        </button>
      </div>
      {expanded && (
        <div className="admin-lesson-content">
          {lesson.contentBlocks.map((block) =>
            editingBlockId === block.id ? (
              <EditContentBlockForm
                key={block.id}
                block={block}
                onSaved={() => {
                  setEditingBlockId(null);
                  onChanged();
                }}
                onCancel={() => setEditingBlockId(null)}
              />
            ) : (
              <div className="admin-block-row" key={block.id}>
                <span className="badge">{block.type}</span>
                <span className="muted admin-block-preview">
                  {block.type === "text" && block.data.html.replace(/<[^>]+>/g, "").slice(0, 80)}
                  {block.type === "video" && block.data.url}
                  {(block.type === "pdf" || block.type === "download") && block.data.fileName}
                  {block.type === "task" && block.data.instructions}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingBlockId(block.id)}>
                  Edit
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => deleteBlock(block.id)}>
                  Remove
                </button>
              </div>
            )
          )}
          <AddContentBlockForm lessonId={lesson.id} onAdded={onChanged} />
          <div className="unlock-rule-section">
            <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
              Unlock rule (drip campaign)
            </div>
            <UnlockRuleEditor
              lesson={lesson}
              isFirstLesson={meta.isFirst}
              dependencyOptions={meta.options}
              onChanged={onChanged}
            />
          </div>
          <div className="unlock-rule-section">
            <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
              Completion requirement
            </div>
            <WatchTimeRuleEditor lesson={lesson} onChanged={onChanged} />
          </div>
          <QuizEditor
            quiz={lesson.quiz}
            settingsUrl={`/api/admin/lessons/${lesson.id}/quiz`}
            onChanged={onChanged}
            title="Lesson quiz (optional)"
            enableLabel="Require students to pass a quiz before this lesson counts as complete"
          />
        </div>
      )}
    </div>
  );
}

function ChapterCard({ chapter, chapterMeta, lessonMetaById, onChanged }) {
  const [lessonTitle, setLessonTitle] = useState("");

  const addLesson = async (e) => {
    e.preventDefault();
    if (!lessonTitle.trim()) return;
    await api.post(`/api/admin/chapters/${chapter.id}/lessons`, { title: lessonTitle });
    setLessonTitle("");
    onChanged();
  };

  const deleteChapter = async () => {
    if (!window.confirm(`Delete chapter "${chapter.title}" and all its lessons?`)) return;
    await api.del(`/api/admin/chapters/${chapter.id}`);
    onChanged();
  };

  const renameChapter = (title) => api.put(`/api/admin/chapters/${chapter.id}`, { title }).then(onChanged);

  return (
    <div className="admin-chapter-card">
      <div className="admin-chapter-header">
        <h3>
          <EditableTitle title={chapter.title} onSave={renameChapter} />
        </h3>
        <button className="btn btn-ghost btn-sm" onClick={deleteChapter}>
          Delete chapter
        </button>
      </div>
      <div className="unlock-rule-section">
        <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
          Chapter unlock rule (drip campaign)
        </div>
        <ChapterUnlockRuleEditor
          chapter={chapter}
          isFirstChapter={chapterMeta.isFirst}
          dependencyOptions={chapterMeta.options}
          onChanged={onChanged}
        />
      </div>
      {chapter.lessons.map((lesson) => (
        <LessonRow lesson={lesson} key={lesson.id} meta={lessonMetaById[lesson.id]} onChanged={onChanged} />
      ))}
      <form onSubmit={addLesson} className="inline-form inline-form-sm">
        <input placeholder="New lesson title" value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} />
        <button className="btn btn-secondary btn-sm">Add lesson</button>
      </form>
    </div>
  );
}

function ContentTab({ courseId, chapters, onChanged }) {
  const [chapterTitle, setChapterTitle] = useState("");

  const addChapter = async (e) => {
    e.preventDefault();
    if (!chapterTitle.trim()) return;
    await api.post(`/api/admin/courses/${courseId}/chapters`, { title: chapterTitle });
    setChapterTitle("");
    onChanged();
  };

  // Dependency-picker options are computed course-wide (not per-chapter) so
  // a section can depend on any earlier section, even one in a previous
  // chapter, and defaults to whichever one immediately precedes it.
  const flatLessons = chapters.flatMap((c, ci) => c.lessons.map((l) => ({ ...l, chapterTitle: c.title, chapterIndex: ci })));
  const lessonMetaById = {};
  flatLessons.forEach((l, idx) => {
    lessonMetaById[l.id] = {
      isFirst: idx === 0,
      options: flatLessons.slice(0, idx).map((o) => ({ id: o.id, label: `${o.chapterTitle} → ${o.title}` })),
      defaultId: idx > 0 ? flatLessons[idx - 1].id : null,
    };
  });

  const chapterMetaById = {};
  chapters.forEach((c, idx) => {
    chapterMetaById[c.id] = {
      isFirst: idx === 0,
      options: chapters.slice(0, idx).map((o) => ({ id: o.id, label: o.title })),
      defaultId: idx > 0 ? chapters[idx - 1].id : null,
    };
  });

  return (
    <div>
      <CourseDripDefaults courseId={courseId} onApplied={onChanged} />
      {chapters.map((chapter) => (
        <ChapterCard
          chapter={chapter}
          chapterMeta={chapterMetaById[chapter.id]}
          lessonMetaById={lessonMetaById}
          key={chapter.id}
          onChanged={onChanged}
        />
      ))}
      <form onSubmit={addChapter} className="inline-form">
        <input placeholder="New chapter title, e.g. 'Chapter 3: Closing the Deal'" value={chapterTitle} onChange={(e) => setChapterTitle(e.target.value)} />
        <button className="btn btn-primary">Add chapter</button>
      </form>
    </div>
  );
}

function EnrollExistingForm({ courseId, onEnrolled }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.post(`/api/admin/courses/${courseId}/enroll`, { email: email.trim() });
      setSuccess(res.reactivated ? `Re-enrolled ${res.student.name}.` : `Enrolled ${res.student.name}.`);
      setEmail("");
      onEnrolled();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="inline-form" style={{ marginBottom: 16 }}>
      <input
        type="email"
        placeholder="Student's email (must already have an account) - or your own, to test this course"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button className="btn btn-secondary" disabled={busy}>
        {busy ? "Enrolling…" : "Enroll"}
      </button>
      {error && <div className="alert alert-error" style={{ margin: 0 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ margin: 0 }}>{success}</div>}
    </form>
  );
}

// Creates a brand-new student account (not just enrolling an existing one) -
// for a student who called in, was referred, or came from a spreadsheet, and
// hasn't signed themselves up. Leaving the password field blank auto-generates
// one, shown once in the success message so it can be handed to the student.
function AddNewStudentForm({ courseId, onEnrolled }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.post(`/api/admin/courses/${courseId}/students`, {
        name: name.trim(),
        email: email.trim(),
        password: password.trim(),
      });
      setSuccess(
        res.temporaryPassword
          ? `Created and enrolled ${res.student.name}. Temporary password: ${res.temporaryPassword} (share this with them - it won't be shown again).`
          : `Created and enrolled ${res.student.name}.`
      );
      setName("");
      setEmail("");
      setPassword("");
      onEnrolled();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="inline-form" style={{ marginBottom: 16, flexWrap: "wrap" }}>
      <input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
      <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input
        type="text"
        placeholder="Password (blank = auto-generate)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button className="btn btn-secondary" disabled={busy}>
        {busy ? "Creating…" : "Add student"}
      </button>
      {error && <div className="alert alert-error" style={{ margin: 0, flexBasis: "100%" }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ margin: 0, flexBasis: "100%" }}>{success}</div>}
    </form>
  );
}

function EnrollStudentForm({ courseId, onEnrolled }) {
  const [mode, setMode] = useState("existing");

  return (
    <div>
      <div className="tabs tabs-sub">
        <button
          type="button"
          className={mode === "existing" ? "tab active" : "tab"}
          onClick={() => setMode("existing")}
        >
          Existing student
        </button>
        <button
          type="button"
          className={mode === "new" ? "tab active" : "tab"}
          onClick={() => setMode("new")}
        >
          Add new student
        </button>
      </div>
      {mode === "existing" ? (
        <EnrollExistingForm courseId={courseId} onEnrolled={onEnrolled} />
      ) : (
        <AddNewStudentForm courseId={courseId} onEnrolled={onEnrolled} />
      )}
    </div>
  );
}

function StudentsTab({ courseId }) {
  const [students, setStudents] = useState(null);

  const load = () => api.get(`/api/admin/courses/${courseId}/students`).then((d) => setStudents(d.students));

  useEffect(() => { load(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeStudent = async (enrollmentId, name) => {
    if (!window.confirm(`Remove ${name}'s enrollment? They'll lose access immediately.`)) return;
    await api.del(`/api/admin/enrollments/${enrollmentId}`);
    load();
  };

  return (
    <div>
      <EnrollStudentForm courseId={courseId} onEnrolled={load} />
      {!students ? (
        <p className="muted">Loading…</p>
      ) : students.length === 0 ? (
        <p className="muted">No students enrolled yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Certificate</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.enrollmentId}>
                <td>
                  {s.name}
                  <div className="muted">{s.email}</div>
                </td>
                <td>
                  <span className={`badge ${s.status === "active" ? "badge-success" : "badge-warning"}`}>{s.status}</span>
                </td>
                <td>
                  {s.progress.completedLessons} / {s.progress.totalLessons}
                </td>
                <td>{s.certificateCode || "—"}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={() => removeStudent(s.enrollmentId, s.name)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Unlike a lesson quiz, the final exam has no questions of its own to
// author - it randomly draws `examLength` questions from the pool of every
// lesson-quiz question in the course each time a student takes it, and
// guarantees at least 70% of a retake's questions are new vs. the
// student's last attempt. So this editor is settings-only: enable, pass
// percent, exam length, and a read-only count of how many pooled questions
// are currently available (so an admin knows whether they've added enough
// lesson-quiz content to support the exam length they've chosen).
function FinalExamEditor({ quiz, settingsUrl, onChanged }) {
  const [enabled, setEnabled] = useState(!!quiz);
  const [passPercent, setPassPercent] = useState(quiz ? quiz.pass_percent : 70);
  const [examLength, setExamLength] = useState(quiz ? quiz.exam_length || 10 : 10);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setEnabled(!!quiz);
    setPassPercent(quiz ? quiz.pass_percent : 70);
    setExamLength(quiz ? quiz.exam_length || 10 : 10);
  }, [quiz?.id, quiz?.pass_percent, quiz?.exam_length]);

  const dirty =
    enabled !== !!quiz ||
    (enabled &&
      (Number(passPercent) !== Number(quiz ? quiz.pass_percent : 70) ||
        Number(examLength) !== Number(quiz ? quiz.exam_length || 10 : 10)));

  const poolSize = quiz ? quiz.poolSize : 0;

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.put(settingsUrl, {
        enabled,
        passPercent: Number(passPercent) || 70,
        examLength: Number(examLength) || 10,
      });
      setSaved(true);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-rule-section">
      <div className="muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
        Final exam
      </div>
      <div className="unlock-rule-editor">
        <label className="checkbox-row">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Require students to pass a final exam for a certificate
        </label>
        {enabled && (
          <div style={{ display: "flex", gap: 16 }}>
            <label style={{ maxWidth: 220 }}>
              Passing score (%)
              <input
                type="number"
                min={1}
                max={100}
                value={passPercent}
                onChange={(e) => setPassPercent(e.target.value)}
              />
            </label>
            <label style={{ maxWidth: 220 }}>
              Questions per attempt
              <input
                type="number"
                min={1}
                value={examLength}
                onChange={(e) => setExamLength(e.target.value)}
              />
            </label>
          </div>
        )}
        <button className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
          {busy ? "Saving…" : dirty ? "Save" : "Saved ✓"}
        </button>
      </div>
      {enabled && (
        <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>
          {poolSize === 0
            ? "No questions available yet - add quiz questions to at least one lesson first (the final exam draws from that same pool, it doesn't have questions of its own)."
            : `${poolSize} question${poolSize === 1 ? "" : "s"} currently available across your lesson quizzes to draw from.`}
          {poolSize > 0 && poolSize < Number(examLength) && (
            <> Since that's fewer than the {examLength} questions you've asked for per attempt, every available
            question will be used each time until you add more.</>
          )}
        </p>
      )}
    </div>
  );
}

function FinalExamTab({ course, onChanged }) {
  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        An optional exam covering the whole course. If enabled, a student must pass it (in addition to completing
        every lesson) before their certificate is issued. Its questions are drawn automatically from your lesson
        quizzes - add/edit those on each lesson in the Content tab; there's nothing to author here.
      </p>
      <FinalExamEditor
        quiz={course.finalExam}
        settingsUrl={`/api/admin/courses/${course.id}/final-exam`}
        onChanged={onChanged}
      />
    </div>
  );
}

export default function AdminCourseEditPage({ id }) {
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [tab, setTab] = useState("content");
  const [error, setError] = useState("");

  const load = () =>
    api
      .get(`/api/admin/courses/${id}`)
      .then((d) => {
        setCourse(d.course);
        setChapters(d.chapters);
      })
      .catch((e) => setError(e.message));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className="page"><div className="alert alert-error">{error}</div></div>;
  if (!course) return <div className="page"><p className="muted">Loading…</p></div>;

  return (
    <div className="page">
      <Link to="/admin" className="player-back">&larr; All courses</Link>
      <h1>{course.title}</h1>
      <div className="tabs">
        <button className={tab === "content" ? "tab active" : "tab"} onClick={() => setTab("content")}>
          Content
        </button>
        <button className={tab === "settings" ? "tab active" : "tab"} onClick={() => setTab("settings")}>
          Settings
        </button>
        <button className={tab === "final-exam" ? "tab active" : "tab"} onClick={() => setTab("final-exam")}>
          Final Exam
        </button>
        <button className={tab === "students" ? "tab active" : "tab"} onClick={() => setTab("students")}>
          Students
        </button>
      </div>

      {tab === "content" && <ContentTab courseId={course.id} chapters={chapters} onChanged={load} />}
      {tab === "settings" && (
        // Merge rather than replace - the settings PUT response doesn't
        // carry the embedded `finalExam` field the course-detail GET
        // includes, and a plain replace would wipe it from local state.
        <CourseSettingsTab course={course} onSaved={(updated) => setCourse((c) => ({ ...c, ...updated }))} />
      )}
      {tab === "final-exam" && <FinalExamTab course={course} onChanged={load} />}
      {tab === "students" && <StudentsTab courseId={course.id} />}
    </div>
  );
}
